import { useContext, useState, useMemo, useEffect } from "react";
import { useRoutes, useNavigate } from "react-router-dom";
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider
} from "@mui/material";
import GlobalStyles from "./global/GlobalStyles";

import Routes from "./Routes";

import { getDesignTokens } from "./global/DefaultTheme";

import ReactGA from "react-ga4";
import { ColorModeContext } from "./global/ColorModeContext";
import {
  findGetParameter,
  setCachedItems
} from "./components/utilities/UtilityFunctions";
import { AuthContext } from "./components/user/BCPAuthProvider";
import ModalLoadingFull from "./components/modals/ModalLoadingFull";
import AppOverlay, {
  isAppMode,
  isAppModeNavDisabled,
  isModalEmbed
} from "./global/AppOverlay";
import { getEnvironmentVariable } from "./global/environmentVariables";
import moment from "moment";
import Maintenance from "./pages/utility/Maintenance";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  getLocalStorage,
  hasCookiePermission,
  setLocalStorage,
  setSessionStorage
} from "./components/layout/cookies/CookieFunctions";

const maintenanceMode = getEnvironmentVariable("MAINTENACE_MODE") === "true";
const googleAnalytics = getEnvironmentVariable("GOOGLE_ANALYTICS");
const IS_GW = getEnvironmentVariable("COMPANY_FLAG") === "gw";

if (googleAnalytics && hasCookiePermission("googleAnalytics")) {
  ReactGA.initialize(googleAnalytics);
  ReactGA.send("pageview");
}

function App() {
  var authContext = useContext(AuthContext);
  const appMode = isAppMode();
  const modalEmbed = isModalEmbed();
  const appModeNavDisabled = isAppModeNavDisabled();

  // States that were previously in Routes function. These need to be here to avoid hooks in regular functions
  const [eventObject, setEventObject] = useState(null);
  const [itemsMiddle, setItemsMiddle] = useState([]);

  const navigate = useNavigate();
  if (window.location.pathname.includes("//")) {
    navigate(
      `${window.location.pathname.replace("//", "/")}${
        window.location.search && window.location.search !== ""
          ? `${window.location.search}`
          : ""
      }`,
      {
        replace: true
      }
    );
  }

  if (
    authContext?.authState?.identity?.betaUser === "true" ||
    authContext?.authState?.identity?.isAdmin === true
  ) {
    if (getLocalStorage("allowTranslations") !== "true") {
      setLocalStorage("allowTranslations", "true");
    }
  }

  const prefersDarkMode = window.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;
  const _darkMode = findGetParameter("darkMode") === "true";
  const _lightMode = findGetParameter("lightMode") === "true";

  const [mode, setMode] = useState(
    _darkMode
      ? "dark"
      : _lightMode
        ? "light"
        : getLocalStorage("themeMode") || (prefersDarkMode ? "dark" : "light")
  );
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      }
    }),
    []
  );

  const [locale, setLocale] = useState(
    getLocalStorage("locale")
      ? getLocalStorage("locale")
      : IS_GW
        ? "en-gb"
        : "en-us"
  );

  useEffect(() => {
    if (appMode) {
      setSessionStorage("appMode", true);
    }

    if (appModeNavDisabled) {
      setSessionStorage("appModeNavDisabled", true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mode) {
      setCachedItems("themeMode", mode);
    }
  }, [mode]);

  if (moment.locale() !== locale) {
    moment.locale(locale);
  }

  useEffect(() => {
    setLocale(getLocalStorage("locale"));
    //eslint-disable-next-line
  }, [getLocalStorage("locale")]);

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const routing = useRoutes(
    Routes(
      authContext,
      itemsMiddle,
      setItemsMiddle,
      eventObject,
      setEventObject
    )
  );
  return (
    <StyledEngineProvider injectFirst>
      <ColorModeContext.Provider value={colorMode}>
        <LocalizationProvider dateAdapter={AdapterMoment} locale={locale}>
          <ThemeProvider theme={theme}>
            <GlobalStyles />
            {maintenanceMode ? <Maintenance /> : routing}
            {authContext?.authState?.processing && <ModalLoadingFull />}
            {appMode && !modalEmbed && !appModeNavDisabled && <AppOverlay />}
          </ThemeProvider>
        </LocalizationProvider>
      </ColorModeContext.Provider>
    </StyledEngineProvider>
  );
}

export default App;
